import React, { useCallback, useMemo, VoidFunctionComponent } from 'react';
import { prepareProps } from 'record-it-components/utils/propUtils';
import {
  SearchableSelect,
  SearchableSelectProps
} from 'record-it-components/input/SearchableSelect/SearchableSelect';
import styles from './ExportYearSelect.module.css';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { useTranslation } from 'react-i18next';

export type ExportYearSelectProps = Omit<
  SearchableSelectProps<number>,
  | 'label'
  | 'items'
  | 'getDisplayTextOfItem'
  | 'getKeyOfItem'
  | 'value'
  | 'onValueChanged'
  | 'className'
> & {
  /**
   * Year selected by default.
   *
   * @default current year
   */
  year: number;
  /**
   * Called whenever the user selects a new year.
   */
  onYearSelected: (selectedYear: number) => void;
};

export const ExportYearSelect: VoidFunctionComponent<ExportYearSelectProps> =
  React.memo((props) => {
    const currentYear = new Date().getFullYear();
    const {
      year = currentYear,
      onYearSelected,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const yearsToSelect = useMemo(
      () => getYears(2022, currentYear),
      [currentYear]
    );
    const getDisplayTextOfYear = useCallback(
      (yearToSelect: number): string => yearToSelect.toString(),
      []
    );
    const getKeyOfYear = useCallback(
      (yearToSelect: number) => yearToSelect.toString(),
      []
    );
    const handleYearSelected = useEventCallback((selectedYear: number) => {
      onYearSelected(selectedYear);
    });

    return (
      <SearchableSelect
        {...remainingProps}
        label={t('components.workTime.ExportYearSelect.label')}
        items={yearsToSelect}
        getDisplayTextOfItem={getDisplayTextOfYear}
        getKeyOfItem={getKeyOfYear}
        value={year}
        onValueChanged={handleYearSelected}
        className={styles.yearSelect}
        data-testid="ExportYearSelect"
      ></SearchableSelect>
    );
  });

/**
 * Returns an array of all years from `from` (inclusive) to `to` (inclusive).
 * @param from The first year in the array.
 * @param to The last year in the array.
 */
function getYears(from: number, to: number): Array<number> {
  const years: Array<number> = [];
  for (let i = from; i <= to; i++) {
    years.push(i);
  }
  return years;
}
